import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { assetUrl } from "../../utils/api";
import { getUserId, planetGet } from "../../api/planetApi";
import {
  destroy,
  fitBounds,
  initMap,
  setClusters,
  setMarkers,
} from "../../planet/mapAdapter";

export default function PlanetSinglesMapPage() {
  const userId = getUserId();
  const mapRef = useRef(null);
  const adapterRef = useRef(null);
  const [card, setCard] = useState(null);
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);

  const loadMapData = async (bounds) => {
    if (!bounds) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        west: String(bounds.getWest()),
        south: String(bounds.getSouth()),
        east: String(bounds.getEast()),
        north: String(bounds.getNorth()),
      });
      if (userId) params.set("viewer_id", String(userId));
      const data = await planetGet(`/singles/map?${params}`);
      const markers = (data.markers || []).map((m) => ({
        lat: m.fuzzy_lat,
        lng: m.fuzzy_lng,
        title: `${m.nickname || "会员"} · ${m.city}`,
        userId: m.user_id,
        raw: m,
        onClick: () => setCard(m),
      }));
      if (data.me?.fuzzy_lat != null) {
        markers.push({
          lat: data.me.fuzzy_lat,
          lng: data.me.fuzzy_lng,
          title: `我 · ${data.me.city || ""}`,
          userId: data.me.user_id,
          raw: data.me,
          onClick: () => setCard({ ...data.me, nickname: "我" }),
        });
      }
      const clusters = (data.clusters || []).map((c) => ({
        lat: c.lat,
        lng: c.lng,
        label: `${c.city}：有人`,
        onClick: () =>
          setHint(`${c.city} 当前人数较少，仅显示「有人」，不展示精确人数与个人标记。`),
      }));
      setMarkers(adapterRef.current, markers);
      setClusters(adapterRef.current, clusters);
      if (!markers.length && !clusters.length) {
        setHint("视野内暂无公开位置的会员。可先到隐私设置公开你的城市。");
      } else if (data.me) {
        setHint((prev) => prev || "地图上的「我」是你的模糊位置（仅自己可见标识）。");
      }
    } catch (e) {
      setHint(e.message || "地图加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!mapRef.current || adapterRef.current) return;
    const adapter = initMap(mapRef.current, { center: [31.2, 117.3], zoom: 5 });
    adapterRef.current = adapter;
    const onMove = () => loadMapData(adapter.map.getBounds());
    adapter.map.on("moveend", onMove);
    loadMapData(adapter.map.getBounds());
    // 中国大致范围
    fitBounds(adapter, [
      { lat: 18, lng: 73 },
      { lat: 53, lng: 135 },
    ]);

    return () => {
      adapter.map.off("moveend", onMove);
      destroy(adapter);
      adapterRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-600">
          仅展示已公开模糊位置的会员；人数不足阈值的城市只显示「有人」。
        </p>
        <Link to="/planet/settings/privacy" className="text-sm text-teal-700 underline">
          设置我的位置
        </Link>
      </div>

      <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
        <div ref={mapRef} style={{ height: 480, width: "100%" }} />
        {loading && (
          <div className="absolute top-3 right-3 text-xs bg-white/90 px-2 py-1 rounded shadow">
            刷新中…
          </div>
        )}
      </div>

      {hint && <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{hint}</p>}

      {card && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex gap-4 items-start">
          <img
            src={assetUrl(card.avatar) || "https://picsum.photos/64"}
            alt=""
            className="w-14 h-14 rounded-full object-cover bg-slate-100"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-teal-950">{card.nickname}</h3>
            <p className="text-sm text-slate-500">{card.city}</p>
            {card.interests && (
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{card.interests}</p>
            )}
            <p className="text-xs text-slate-400 mt-2">
              {card.accept_bottle ? "愿意接漂流瓶" : "暂不接瓶"}
              {card.accept_invite ? " · 可发认识邀请" : ""}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to={`/user/${card.user_id}`}
                className="text-sm px-3 py-1.5 rounded-lg bg-teal-800 text-white"
              >
                查看资料
              </Link>
              <button
                type="button"
                className="text-sm px-3 py-1.5 rounded-lg border"
                onClick={() => setCard(null)}
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
